'use client';

import { cn } from '@/lib/utils';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import * as React from 'react';

interface CheckboxCardsProps {
  options: Array<{ value: string; label: string }>;
  selectedValues: string[];
  onValueChange: (values: string[]) => void;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'surface' | 'classic';
  columns?: string;
}

const CheckboxCards = React.forwardRef<HTMLDivElement, CheckboxCardsProps>(
  (
    {
      options,
      selectedValues,
      onValueChange,
      size = 'md',
      variant = 'surface',
      columns = '1',
    },
    ref
  ) => {
    const handleValueChange = (value: string, checked: boolean) => {
      if (checked) {
        onValueChange([...selectedValues, value]);
      } else {
        onValueChange(selectedValues.filter(v => v !== value));
      }
    };

    const handleCardClick = (value: string) => {
      const isCurrentlySelected = selectedValues.includes(value);
      handleValueChange(value, !isCurrentlySelected);
    };

    return (
      <div
        ref={ref}
        className={cn(
          'grid gap-2',
          columns === '1' ? 'grid-cols-1' : 'grid-cols-2'
        )}
      >
        {options.map(option => (
          <div
            key={option.value}
            onClick={() => handleCardClick(option.value)}
            className={cn(
              'relative flex items-center space-x-2 rounded-lg border p-3 transition-all duration-200 cursor-pointer',
              variant === 'surface'
                ? 'bg-background hover:bg-accent hover:text-accent-foreground'
                : 'bg-card hover:bg-accent hover:text-accent-foreground',
              selectedValues.includes(option.value) &&
                'border-primary bg-primary/5 shadow-sm',
              size === 'sm' && 'p-1.5',
              size === 'lg' && 'p-4'
            )}
          >
            <CheckboxPrimitive.Root
              id={option.value}
              checked={selectedValues.includes(option.value)}
              onCheckedChange={checked =>
                handleValueChange(option.value, checked as boolean)
              }
              className={cn(
                'peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
                selectedValues.includes(option.value) &&
                  'border-primary bg-primary'
              )}
              onClick={e => e.stopPropagation()} // 체크박스 클릭 시 카드 클릭 이벤트 방지
            >
              <CheckboxPrimitive.Indicator
                className={cn('flex items-center justify-center text-current')}
              >
                <Check className='h-4 w-4' />
              </CheckboxPrimitive.Indicator>
            </CheckboxPrimitive.Root>
            <label
              htmlFor={option.value}
              className={cn(
                'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer select-none flex-1',
                size === 'sm' && 'text-xs',
                size === 'lg' && 'text-base'
              )}
            >
              {option.label}
            </label>
          </div>
        ))}
      </div>
    );
  }
);
CheckboxCards.displayName = 'CheckboxCards';

export { CheckboxCards };
